import { AsyncPipe, Location, NgClass } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  HostListener,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  ViewEncapsulation,
  WritableSignal
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogConfig, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { MessagingService } from '../components/messaging/messaging.service';
import { extraWideModalConfig, mediumModalConfig, wideModalConfig } from '../consts/modal-config.const';
import { SocketClientState } from '../enums/socketClientState.enum';
import { BodyParserHelper } from '../helpers/body-parser.helper';
import { CleanActiveJobHelper } from '../helpers/clean-active-job.helper';
import { ModalConfigsHelper } from '../helpers/modal-configs.helper';
import { Refreshable } from '../model/refreshable.model';
import { User } from '../model/user.model';
import { CurrentUserService } from '../services/currentUser.service';
import { NoticeService } from '../services/notice.service';
import { SysAdminService } from '../services/sys-admin.service';
import { ToolbarService } from '../services/toolbar.service';
import { UserService } from '../services/user.service';
import { WebRTCService } from '../services/webRTC.service';
import { WebsocketService } from '../services/websocket.service';
import { Privileges } from '../types/privileges.type';
import { ConfirmShareScreenComponent } from './confirm-share-screen/confirm-share-screen.component';
import { JobSearchComponent } from './job-search/job-search.component';
import { AcknowledgeNoticeModalComponent } from './notice-board/acknowledge-notice-modal.component';
import { AccessRepairerSiteComponent } from './sysadmin-dashboard/access-repairer-site/access-repairer-site.component';
import { MatToolbar, MatToolbarRow } from '@angular/material/toolbar';
import { MatIcon } from '@angular/material/icon';
import { RefreshComponent } from '../components/refresh/refresh.component';
import { MatFormField, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { BrowserAutoFillDisabledDirective } from '../directives/browser-auto-fill-disabled.directive';
import { MatIconButton } from '@angular/material/button';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { RepairerWarningBanner } from '../components/repairer-warning-banner/repairer-warning-banner';
import { MatDivider } from '@angular/material/divider';
import { BreakObserverPipe } from '../pipes/breakObserver.pipe';
import { SupportMenuContentComponent } from './support-menu-content/support-menu-content.component';

@Component({
  selector: 'app-shell',
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    MatToolbar,
    MatToolbarRow,
    MatIcon,
    RefreshComponent,
    ReactiveFormsModule,
    MatFormField,
    NgClass,
    MatPrefix,
    MatInput,
    BrowserAutoFillDisabledDirective,
    MatSuffix,
    MatIconButton,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    RepairerWarningBanner,
    RouterOutlet,
    MatDivider,
    AsyncPipe,
    BreakObserverPipe,
    SupportMenuContentComponent
  ]
})
export class ShellComponent implements OnInit, OnDestroy {
  @ViewChild('inputSearch', { static: false }) inputSearch: ElementRef<HTMLElement>;
  currentYear = new Date().getFullYear();
  currentUser: User;
  timezone: string;
  pageName: string;
  backLink: string;
  rego: string;
  rtcConfig: any;
  routedComponent: Refreshable;
  subscription: Subscription;
  subscriptionToWebsocket: Subscription;
  subscriptionToWebRTC: Subscription;
  searchJobsForm: UntypedFormGroup;
  userMenuOpened = false;
  supportMenuOpened = false;
  screenShareStarted: WritableSignal<boolean> = signal(false);
  screenShareConfirmationOpened = false;
  searchVal: BehaviorSubject<string>;
  backLinkActive = false;
  backLinkText: string;
  shareScreenDialogRef: MatDialogRef<ConfirmShareScreenComponent>;
  isRepadmin: boolean;
  isRepairer: boolean;
  isSysadmin: boolean;
  isMlAdmin: boolean;
  private unsubscribe = new Subject<void>();

  currentUserService = inject(CurrentUserService);
  sysAdminService = inject(SysAdminService);
  toolbarService = inject(ToolbarService);
  userService = inject(UserService);
  #injector = inject(Injector);
  cleanActiveJobHelper = inject(CleanActiveJobHelper);
  #router = inject(Router);
  #cdRef = inject(ChangeDetectorRef);
  #fb = inject(UntypedFormBuilder);
  #location = inject(Location);
  #auth = inject(AuthService);
  #noticeService = inject(NoticeService);
  #messageService = inject(MessagingService);
  #webRtcService = inject(WebRTCService);
  #websocketService = inject(WebsocketService);
  #bodyParserHelper = inject(BodyParserHelper);
  #modalConfigsHelper = inject(ModalConfigsHelper);

  // Specific dialog manipulation for jobSearchDialog, #dialog as the generic for other MatDialog applications
  jobSearchDialog = inject(MatDialog);
  #dialog = inject(MatDialog);

  get searchInput() {
    return this.searchJobsForm.get('searchInput');
  }

  ngOnInit() {
    this.sysAdminService.getRepairerSiteAccessFromSessionStorage();
    this.checkUserPrivileges();
    this.checkRepairSiteFlags();
    this.sysAdminService.getFeaturesGated().subscribe();
    this.searchJobsForm = this.#fb.group({ searchInput: ['', []] });
    this.toolbarService.cloudState$.subscribe();
    this.currentUser = this.currentUserService.currentUserValue;
    this.timezone = this.currentUserService?.timeZone;
    this.#websocketService.establishWebSocketConnection('/ws');

    setTimeout(() => {
      if (this.#websocketService.connected && this.currentUserService.currentUserValue?.repairerSiteId) {
        this.subscribeToScreenShare(this.currentUser.username);
      }
    }, 2000);

    this.#websocketService.connectionState.pipe(takeUntil(this.unsubscribe)).subscribe({
      next: (data) => {
        if (data === SocketClientState.ERROR) {
          // Don't reconnect on 401 (Unauthorized) errors
          if (this.#websocketService.lastErrorStatus !== 401) {
            this.#websocketService.establishWebSocketConnection('/ws');
          }
        }
      }
    });

    this.getIceServerConfig();

    this.subscription = this.toolbarService.toolbarDataReceived$.subscribe({
      next: (data) => {
        this.checkUserPrivileges();
        this.pageName = data.pageName;
        this.backLink = data.backLink;
        this.rego = data.rego;
        this.#cdRef.detectChanges();
      }
    });

    this.activateBackLink(sessionStorage.getItem('previousUrl'));

    this.#router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.unsubscribe)
      )
      .subscribe((event: NavigationEnd) => {
        this.activateBackLink(event.url);
        this.sysAdminService.getRepairerSiteAccessFromSessionStorage();
      });

    this.userService.getRepairerGroup().pipe(takeUntil(this.unsubscribe)).subscribe();

    effect(
      () => {
        if (this.#noticeService.unseenNotices()?.length > 0) {
          const data = { notices: this.#noticeService.unseenNotices() };
          this.#dialog.open(AcknowledgeNoticeModalComponent, { ...wideModalConfig, data });
        }
      },
      { injector: this.#injector }
    );
  }

  private checkUserPrivileges() {
    const userPrivileges = this.currentUserService.currentUserValue.privileges || [];

    this.isRepairer = !userPrivileges.some((item) => ['REPADMIN', 'MLADMIN', 'SYSADMIN'].includes(item));
    this.isRepadmin =
      this.hasPrivilege(userPrivileges, 'REPADMIN') &&
      !this.hasPrivilege(userPrivileges, 'SYSADMIN') &&
      !this.hasPrivilege(userPrivileges, 'MLADMIN');
    this.isMlAdmin = this.hasPrivilege(userPrivileges, 'MLADMIN') && !this.hasPrivilege(userPrivileges, 'REPADMIN');
    this.isSysadmin = this.hasPrivilege(userPrivileges, 'SYSADMIN') && !this.hasPrivilege(userPrivileges, 'REPADMIN');
  }

  private checkRepairSiteFlags(): void {
    this.currentUserService.currentUserValue.repairerSiteId === undefined
      ? this.userService.clearRepairerSiteFlags()
      : this.userService.getRepairerSiteForUser().subscribe();
  }

  private hasPrivilege(privileges: string[], privilegeToCheck: string): boolean {
    return privileges.includes(privilegeToCheck);
  }

  navigateToPage(page) {
    this.#router.navigate([page]).then(() => {
      this.cleanActiveJobHelper.clearSessionStorageFromActiveJob();
      this.jobSearchDialog.closeAll();
    });
  }

  activateBackLink(prevURL: string) {
    if (sessionStorage.getItem('previousUrl')) {
      this.backLinkActive =
        (prevURL.indexOf('jobViewer') > -1 || prevURL.indexOf('claimList') > -1) &&
        this.#router.url.indexOf('jobViewer') > -1 &&
        sessionStorage.getItem('previousUrl') !== 'search';
      this.backLinkText = sessionStorage.getItem('previousUrl').indexOf('jobViewer') > -1 ? 'DASHBOARD' : 'CLAIM LIST';
    } else {
      this.backLinkActive = false;
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    if (this.subscriptionToWebRTC) {
      this.subscriptionToWebRTC.unsubscribe();
    }
    if (this.subscriptionToWebsocket) {
      this.subscriptionToWebsocket.unsubscribe();
    }
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.#websocketService.closeWebSocketConnection();
    this.#noticeService.unseenNotices.set([]);
  }

  backWithLocation() {
    this.#location.back();
  }

  subscribeToScreenShare(repairerUserName: string) {
    this.subscriptionToWebsocket = this.#websocketService.clientStomp?.subscribe(
      `/repuser/screenshare/${repairerUserName}`,
      (event) => {
        const message = this.#bodyParserHelper.checkBodyValue(event.body) ? JSON.parse(event.body) : event.body;
        if (message.event === 'sysAdminRequestShare') {
          this.requestScreenSharing(message.data, repairerUserName);
        }
        if (message === 'STOP') {
          if (this.#webRtcService.displayMediaStream) {
            this.#webRtcService.displayMediaStream.getTracks().forEach((track) => {
              track.stop();
            });
          }
          this.#webRtcService.resetShareScreen();
          this.screenShareStarted.set(false);
        }
        if (message.event === 'stoppingShareScreen' || message.event === 'refreshSysAdmin') {
          this.#messageService.error(`The screen share was canceled by ${message.data}`);
          if (this.shareScreenDialogRef && this.shareScreenDialogRef.getState() === MatDialogState.OPEN) {
            this.shareScreenDialogRef.close();
            this.#webRtcService.resetShareScreen();
          } else {
            if (!this.screenShareStarted()) {
              setTimeout(() => location.reload(), 1000);
            }
          }
          this.screenShareStarted.set(false);
        }
      }
    );
  }

  subscribeToWebRTC(repairerUserName: string) {
    this.subscriptionToWebRTC = this.#websocketService.clientStomp.subscribe(
      `/repuser/webRTC/receiveSignal/${repairerUserName}`,
      (message) => {
        const content = JSON.parse(message.body);
        const data = content.data;
        switch (content.event) {
          case 'answer':
            this.#webRtcService.handleAnswer(data).then();
            break;
          case 'candidate':
            this.#webRtcService.handleCandidate(data).then();
            break;
          case 'stoppingShareScreen':
            this.#messageService.error(`The screen share was stopped by ${data}`);
            setTimeout(() => {
              if (this.#webRtcService.displayMediaStream) {
                this.#webRtcService.displayMediaStream.getTracks().forEach((track) => {
                  track.stop();
                });
              }
              this.#webRtcService.resetShareScreen();
            }, 500);
            break;
        }
      }
    );
  }

  getIceServerConfig() {
    if (!sessionStorage.getItem('RTCConfiguration')) {
      this.userService
        .getConfig()
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: (configuration: RTCConfiguration) => {
            this.rtcConfig = {
              iceServers: configuration,
              sdpSemantics: 'unified-plan',
              bundlePolicy: 'max-compat'
            };
            sessionStorage.setItem('RTCConfiguration', JSON.stringify(this.rtcConfig));
          },
          error: () => this.#messageService.error('Failed to get RTCConfiguration')
        });
    } else {
      this.rtcConfig = JSON.parse(sessionStorage.getItem('RTCConfiguration'));
    }
  }

  requestScreenSharing(sysAdminName: string, repairerUserName: string) {
    this.screenShareConfirmationOpened = true;
    this.shareScreenDialogRef = this.#dialog.open(
      ConfirmShareScreenComponent,
      this.#modalConfigsHelper.buildMediumModalConfig({ sysAdminName }, mediumModalConfig)
    );

    this.shareScreenDialogRef.afterClosed().subscribe((result) => {
      if (result === 'ACCEPT') {
        this.#webRtcService.establishWebRtcConnection(this.rtcConfig, `/repuser/webRTC/signal/${repairerUserName}`);
        this.#websocketService.send(`/repuser/screenshare/${repairerUserName}`, {
          event: 'repUserAcceptShare',
          data: this.currentUser.username
        });
        this.subscribeToWebRTC(repairerUserName);
        const displayMediaOptions = {
          video: {
            cursor: 'motion',
            displaySurface: ['browser']
          },
          audio: false,
          selfBrowserSurface: 'include'
        };
        const cursor = document.querySelector('.cursor-shell');
        cursor.setAttribute('style', 'visibility = "visible"');
        this.#webRtcService
          .getScreenSizeOnopen(
            displayMediaOptions,
            cursor,
            `/repuser/webRTC/signal/${repairerUserName}`,
            `${this.currentUser.firstName} ${this.currentUser.lastName}`
          )
          .then(() => {
            this.screenShareStarted.set(true);
          });
      } else {
        this.#websocketService.send(`/repuser/screenshare/${repairerUserName}`, {
          event: 'cancelScreenShare',
          data: `${this.currentUser.firstName} ${this.currentUser.lastName}`
        });
      }
    });
  }

  setRoutedComponent(component: Refreshable) {
    this.routedComponent = component;
    this.#cdRef.detectChanges(); // prevent 'expression has changed after it was checked' error
    this.#auth.refresh().subscribe();

    if (this.sysAdminService.repairerSiteAccessed()) {
      this.subscribeToScreenShare(this.sysAdminService.repairerSiteAccessed());
    }
  }

  logout = () =>
    this.#auth
      .logout()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: () => {
          this.#router.navigate(['login']).catch(() => window.location.replace('/'));
          this.currentUserService.userLogout();
        },
        error: () => this.#messageService.error('There was a problem logging you out. Please try again.'),
        complete: () => {
          sessionStorage.clear();
          if (this.screenShareConfirmationOpened) {
            this.#websocketService.send(`/repuser/screenshare/${this.currentUser.repairerSiteId}`, {
              event: 'userLeftApp',
              data: `${this.currentUser.firstName} ${this.currentUser.lastName}`
            });
            if (this.#webRtcService.displayMediaStream) {
              this.#webRtcService.displayMediaStream.getTracks().forEach((track) => {
                track.stop();
              });
            } else {
              window.location.reload();
            }
          }
        }
      });

  exitRepairerSite() {
    const data = { repairerSiteName: this.sysAdminService.repairerSiteAccessed() };
    this.#dialog
      .open(AccessRepairerSiteComponent, this.#modalConfigsHelper.buildMediumModalConfig(data, mediumModalConfig))
      .afterClosed()
      .subscribe({
        next: (response) => {
          if (response?.result === 'CLEAR') {
            sessionStorage.removeItem('timeZone');
          }
        }
      });
  }

  menuOpened() {
    this.userMenuOpened = true;
  }

  searchJob() {
    if (!this.jobSearchDialog?.openDialogs.length) {
      const dialogConfig: MatDialogConfig = extraWideModalConfig;
      dialogConfig.panelClass = 'full-screen-dialog';
      dialogConfig.backdropClass = 'search-screen';
      dialogConfig.disableClose = false;
      dialogConfig.data = {
        searchValue: new BehaviorSubject(this.searchInput.value),
        userPrivilege: this.fetchTopUserPrivilege()
      };
      this.jobSearchDialog.open(JobSearchComponent, dialogConfig);
    } else {
      this.jobSearchDialog.openDialogs[0].componentInstance.data.searchValue.next(this.searchInput.value);
    }
    this.jobSearchDialog.afterAllClosed.subscribe({
      next: () => {
        this.searchInput.setValue('');
        this.inputSearch.nativeElement.blur();
      }
    });
  }

  private fetchTopUserPrivilege(): 'SYSADMIN' | 'REPADMIN' | 'MLADMIN' | null {
    const privilegeArray: Privileges[] = ['SYSADMIN', 'REPADMIN', 'MLADMIN'];
    const currentUserPrivileges = this.currentUserService.currentUserValue.privileges || [];
    const foundPrivilege = currentUserPrivileges.find((privilege) => privilegeArray.includes(privilege));

    return foundPrivilege || null;
  }

  @HostListener('window:beforeunload')
  goToPage() {
    if (this.currentUserService.currentUserValue?.repairerSiteId) {
      this.#websocketService.send(`/repuser/screenshare/${this.currentUser.username}`, {
        event: 'refreshRepUser',
        data: `${this.currentUser.firstName} ${this.currentUser.lastName}`
      });
    }
  }

  @HostListener('window:blur', ['$event'])
  onBlur(event: any): void {
    if (event && this.currentUserService.currentUserValue?.repairerSiteId && this.screenShareStarted()) {
      this.#websocketService.send(`/repuser/screenshare/${this.currentUser.username}`, {
        event: 'pauseScreenShare',
        data: true
      });
    }
  }

  @HostListener('window:focus', ['$event'])
  onFocus(event: FocusEvent): void {
    if (event && this.currentUserService.currentUserValue?.repairerSiteId && this.screenShareStarted()) {
      this.#websocketService.send(`/repuser/screenshare/${this.currentUser.username}`, {
        event: 'pauseScreenShare',
        data: false
      });
    }
  }
}
